import { getCollectionCapacityCapability } from '../../firebase/functions';
import {
  subscribeToCollection,
  subscribeToCollectionMembership,
} from '../../repositories/collectionRepository';
import { CollectionDataStore } from './collectionDataStore';
import {
  loadInventoryPage,
  loadInventoryRecord,
} from '../../repositories/multiCollectionInventoryRepository';
import {
  createInventoryMetadataCache,
  clearInventoryMetadataCaches,
} from './inventoryMetadataCache';
import { subscribeInventoryInvalidations } from './inventoryInvalidation';

const sessions = new Map<string, CollectionDataStore>();

export function getCollectionDataStore(userId: string): CollectionDataStore {
  let store = sessions.get(userId);
  if (!store) {
    store = new CollectionDataStore(userId, {
      membership: (id, userId, next, error) =>
        subscribeToCollectionMembership(id, userId, next, error, true),
      collection: (id, next, error) =>
        subscribeToCollection(id, next, error, true),
      cache: createInventoryMetadataCache(userId),
      record: (collectionId, kind, recordId) =>
        loadInventoryRecord(
          { userId, collectionId, compatibilityMode: 'collection-only' },
          kind,
          recordId,
        ),
      capacity: async ({ collectionId, membershipVersion }) =>
        (
          await getCollectionCapacityCapability({
            collectionId,
            expectedMembershipVersion: membershipVersion,
          })
        ).data,
      page: (collectionId, kind, cursor, sort) => {
        const authority = {
          userId,
          collectionId,
          compatibilityMode: 'collection-only' as const,
        };
        return kind === 'items'
          ? sort
            ? loadInventoryPage(authority, 'items', cursor, sort)
            : loadInventoryPage(authority, 'items', cursor)
          : loadInventoryPage(authority, 'locations', cursor);
      },
    });
    sessions.set(userId, store);
  }
  return store;
}

// One dispatcher updates active and inactive cached resources without making
// mutation callers depend on the store or creating stores for unseen users.
subscribeInventoryInvalidations((change) =>
  sessions.get(change.userId)?.inventory.applyChange(change),
);

/** Dispose session authority on every transition; preserve disk on ordinary remounts. */
export function resetCollectionDataSession(purgePersistent = false) {
  const previous = [...sessions.values()];
  sessions.clear();
  previous.forEach((store) => store.dispose());
  if (purgePersistent) void clearInventoryMetadataCaches();
}

export const subscribeToSharedCollectionMembership: typeof subscribeToCollectionMembership =
  (id, userId, next, error) =>
    getCollectionDataStore(userId).subscribeMembership(id, next, error);

export const subscribeToSharedCollection = (
  id: string,
  userId: string,
  next: Parameters<typeof subscribeToCollection>[1],
  error: Parameters<typeof subscribeToCollection>[2],
) => getCollectionDataStore(userId).subscribeCollection(id, next, error);
